import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { Label, LabelEntityType, CreateLabelRequest } from '../../types/labels';
import {
	labelsOnPartsRestCrudThunksForSupplier,
	associateLabelsWithEntity,
} from '../../thunks/labels_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { stopDefaultEvents, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { pickRandomLabelColor } from '../../constants/labelConstants';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { AddLabelModal, AddLabelModalLabel } from '../StarLabels/AddLabelModal';
import PartsLabelsManagementPage from '../parts_labels_management_page/PartsLabelsManagementPage';

// Private to this modal's server-side search so it never clobbers the `PART`
// collection the Parts index sidebar and create flow read.
const PART_LABELS_DROPDOWN_TC = 'partLabelsDropdown';
// Parts inline-create requires a 3+ char search before offering "Create New Label".
const PARTS_MIN_CREATE_LENGTH = 3;

type PartsAddLabelButtonOwnProps = {
	entityType: LabelEntityType;
	entityId: number;
	existingLabels?: AddLabelModalLabel[];
	disabled?: boolean;
	onChange?: (selectedLabels: AddLabelModalLabel[]) => void;
	onSave?: (selectedLabels: AddLabelModalLabel[]) => void;
	refreshTable?: () => void;
};

type PartsAddLabelButtonProps = PartsAddLabelButtonOwnProps & {
	userType: string;
	companyConfig: any;
	currentUser: any;
	catalogue: Label[];
	catalogueFetching: boolean;
	fetchLabels: (search: string) => void;
	createLabel: (label: CreateLabelRequest) => Promise<Label>;
	assignLabels: (entityId: number, labelIds: number[]) => Promise<any>;
};

// Parts binding for the shared AddLabelModal: owns the inline "Add Labels" trigger
// (rendered on part-row hover), wires the parts label thunks, and opens the parts
// label-management drawer behind EDIT. Drop-in replacement for the old LabelSelector.
function PartsAddLabelButton({
	entityType,
	entityId,
	existingLabels,
	disabled = false,
	onChange,
	onSave,
	refreshTable,
	userType,
	companyConfig,
	currentUser,
	catalogue,
	catalogueFetching,
	fetchLabels,
	createLabel,
	assignLabels,
}: PartsAddLabelButtonProps): React.ReactElement {
	const [modalOpen, setModalOpen] = useState(false);

	const canModifyParts = useMemo(
		() =>
			isUserAllowedToAccess(userType)(
				PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[userType, companyConfig, currentUser]
	);

	return (
		<div onClick={stopDefaultEvents}>
			<Button
				type="link"
				size="small"
				icon={<PlusOutlined />}
				disabled={disabled}
				onClick={() => setModalOpen(true)}
			>
				Add Labels
			</Button>
			<AddLabelModal
				visible={modalOpen}
				onClose={() => setModalOpen(false)}
				entityId={entityId}
				existingLabels={existingLabels || []}
				canModifyLabels={canModifyParts}
				canApplyLabels
				catalogue={catalogue}
				catalogueFetching={catalogueFetching}
				fetchLabels={fetchLabels}
				createLabel={createLabel}
				assignLabels={assignLabels}
				pickNewLabelColor={pickRandomLabelColor}
				iconVariant="dot"
				minCreateLength={PARTS_MIN_CREATE_LENGTH}
				onAfterAssign={(_id, savedLabels) => {
					message.success('Labels updated');
					if (refreshTable) refreshTable();
					if (onChange) onChange(savedLabels);
					if (onSave) onSave(savedLabels);
				}}
				onAssignError={() => message.error('Failed to update labels')}
			/>
		</div>
	);
}

const mapStateToProps = (state: RootState) => ({
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
	catalogue: getRecordsForTargetCollection(state.labels, PART_LABELS_DROPDOWN_TC) || [],
	catalogueFetching: nullSafeGetOrElse(`${PART_LABELS_DROPDOWN_TC}.fetching`, state.labels, false),
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: PartsAddLabelButtonOwnProps & { userType: string }
) => ({
	fetchLabels: (search: string) =>
		dispatch(
			labelsOnPartsRestCrudThunksForSupplier.read(
				{ no_pagination: true, ...(search ? { search } : {}) },
				PART_LABELS_DROPDOWN_TC,
				null,
				{ sort_by: 'label', order: 'asc' },
				{},
				false
			)
		),
	// Pass entityType as the 3rd arg (targetCollectionName), not the 2nd (primaryKey): create()
	// is create(entity, primaryKey, targetCollectionName) and only adds the new label to a
	// collection's recordsIds when a targetCollectionName is given. Writing into the entityType
	// collection ('PART') — the one the label filter reads — makes a newly created label appear
	// in the filter reactively (no refetch needed).
	createLabel: (label: CreateLabelRequest) =>
		dispatch(labelsOnPartsRestCrudThunksForSupplier.create(label, 'id', ownProps.entityType)),
	assignLabels: (entityId: number, labelIds: number[]) =>
		dispatch(associateLabelsWithEntity(ownProps.userType)(ownProps.entityType, entityId, labelIds)),
});

// @ts-ignore - Type inference issue with HOC composition, same pattern as PartsLabelCreationButton
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(PartsAddLabelButton));
